import { c as _c } from "react/compiler-runtime";
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { once } from 'es-toolkit';
import nstr from 'nstr';
import { createContext, use, useId, useRef } from 'react';
import { cn } from '@/lib/utils';
import { useIdentity } from '@sub-vocab/utils/hooks';
const segmentedControlVariants = cva(clsx(`grid w-full touch-manipulation! auto-cols-[1fr] grid-flow-col overflow-hidden rounded-lg antialiased outline-hidden select-none [text-rendering:geometricPrecision]`, 'sq:rounded-[.9375rem] sq:superellipse-[1.75]'), {
  variants: {
    variant: {
      default: 'bg-[#EFEFF0] p-0.5 dark:bg-neutral-800/40',
      ghost: 'bg-transparent px-1.5 py-1'
    },
    size: {
      default: '',
      small: '',
      medium: ''
    }
  },
  compoundVariants: [{
    variant: 'default',
    className: 'group/default'
  }, {
    variant: 'ghost',
    className: 'group/ghost'
  }],
  defaultVariants: {
    variant: 'default',
    size: 'default'
  }
});
interface SegmentedControlProps<T extends string> extends React.ComponentProps<'div'>, VariantProps<typeof segmentedControlVariants> {
  value: T;
  onValueChange: (value: T) => void;
}
const checkedSegmentVariants = cva('bg-white transition-transform duration-300', {
  variants: {
    variant: {
      default: 'border-[.5px] border-black/[.1] bg-white drop-shadow-xs dark:bg-neutral-600',
      ghost: 'bg-neutral-200 dark:bg-neutral-600'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
type SegmentContextProps<T> = {
  value: T;
  onValueChange: (value: T) => void;
  variant: VariantProps<typeof segmentedControlVariants>['variant'];
  addToRefs: (key: T) => (el: HTMLDivElement) => void;
};

// https://stackoverflow.com/a/61020816
const createSegmentContext = once(<T,>() => createContext<SegmentContextProps<T> | null>(null));
const useSegmentContext = <T,>() => use(createSegmentContext<T>());
export function SegmentedControl(t0) {
  const $ = _c(30);
  let children;
  let className;
  let props;
  let setValue;
  let size;
  let value;
  let variant;
  if ($[0] !== t0) {
    ({
      value,
      onValueChange: setValue,
      variant,
      size,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = setValue;
    $[5] = size;
    $[6] = value;
    $[7] = variant;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    setValue = $[4];
    size = $[5];
    value = $[6];
    variant = $[7];
  }
  let t1;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const pillRefs = useRef(t1);
  let t2;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = createSegmentContext();
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const SegmentContext = t2;
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = function activatePill(pillRef, previousIndicatorClientRect) {
      const element = pillRef;
      if (!previousIndicatorClientRect || !element.getBoundingClientRect) {
        return;
      }
      const currentClientRect = element.getBoundingClientRect();
      const widthDelta = previousIndicatorClientRect.width / currentClientRect.width;
      const xPosition = previousIndicatorClientRect.left - currentClientRect.left;
      element.style.setProperty("transform", `translateX(${nstr(xPosition)}px) scaleX(${nstr(widthDelta)})`);
      element.getBoundingClientRect();
      element.style.setProperty("transform", "");
    };
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const activatePill = t3;
  let t4;
  if ($[11] !== setValue || $[12] !== value) {
    t4 = newValue => {
      const pillRef_0 = pillRefs.current[newValue];
      if (pillRef_0) {
        const previousPill = pillRefs.current[value];
        const domRect = previousPill?.getBoundingClientRect();
        activatePill(pillRef_0, domRect);
        setValue(newValue);
      }
    };
    $[11] = setValue;
    $[12] = value;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = key => el => {
      pillRefs.current[key] = el;
    };
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4 || $[16] !== value || $[17] !== variant) {
    t6 = {
      value,
      onValueChange: t4,
      variant,
      addToRefs: t5
    };
    $[15] = t4;
    $[16] = value;
    $[17] = variant;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const segmentContextValue = useIdentity(t6);
  let t7;
  if ($[19] !== className || $[20] !== size || $[21] !== variant) {
    t7 = cn(segmentedControlVariants({
      variant,
      size,
      className
    }));
    $[19] = className;
    $[20] = size;
    $[21] = variant;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== children || $[24] !== props || $[25] !== t7) {
    t8 = <div className={t7} {...props}>{children}</div>;
    $[23] = children;
    $[24] = props;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== segmentContextValue || $[28] !== t8) {
    t9 = <SegmentContext value={segmentContextValue}>{t8}</SegmentContext>;
    $[27] = segmentContextValue;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
}
export function SegmentItem(t0) {
  const $ = _c(34);
  const {
    segment
  } = t0;
  const segmentContextValue = useSegmentContext();
  const id = useId();
  const checked = segment.value === segmentContextValue?.value;
  const label = segment.label;
  let t1;
  if ($[0] !== segment.value || $[1] !== segmentContextValue) {
    t1 = () => {
      segmentContextValue?.onValueChange(segment.value);
    };
    $[0] = segment.value;
    $[1] = segmentContextValue;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== checked || $[4] !== id || $[5] !== segment.value || $[6] !== t1) {
    t2 = <input id={id} aria-label="Segmented control" type="radio" value={segment.value} checked={checked} className="absolute inset-0 appearance-none opacity-0 outline-hidden" onChange={t1} />;
    $[3] = checked;
    $[4] = id;
    $[5] = segment.value;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = segmentContextValue?.variant === "ghost" && "leading-5.5";
  let t4;
  if ($[8] !== t3) {
    t4 = clsx("flex flex-col justify-center text-sm/6", t3);
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  let t6;
  if ($[10] !== label) {
    t5 = <div className="relative z-10 flex justify-center text-black transition-opacity duration-200 ease-[ease] will-change-transform group-hover/d:opacity-20 group-focus/d:opacity-20 group-active/d:opacity-20 group-active/d:delay-150 group-data-[checked=true]/d:font-medium group-data-[checked=true]/d:opacity-100 group-[&:active[data-checked=false]]/d:scale-95 dark:text-white">{label}</div>;
    t6 = <div title={label} className="before:invisible before:block before:h-0 before:overflow-hidden before:font-bold before:content-[attr(title)]" />;
    $[10] = label;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <div className={t4}>{t5}{t6}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== segment.value || $[18] !== segmentContextValue) {
    t8 = segmentContextValue?.addToRefs(segment.value);
    $[17] = segment.value;
    $[18] = segmentContextValue;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== checked || $[21] !== segmentContextValue) {
    t9 = cn("flex size-full rounded-md ease-[ease] will-change-transform", "sq:rounded-[.6875rem] sq:superellipse-[1.75]", checked && checkedSegmentVariants({
      variant: segmentContextValue.variant
    }));
    $[20] = checked;
    $[21] = segmentContextValue;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t8 || $[24] !== t9) {
    t10 = <div className="absolute top-0 left-0 size-full"><div ref={t8} className={t9} /></div>;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== id || $[27] !== t10 || $[28] !== t7) {
    t11 = <label htmlFor={id} className="relative block cursor-pointer bg-transparent text-center group-data-[checked=true]/d:cursor-default before:absolute before:inset-y-[14%] before:left-0 before:w-px before:translate-x-[-.5px] before:rounded-[.625rem] before:bg-neutral-300 before:transition-[background] before:duration-200 before:ease-[ease] before:will-change-[background] group-first-of-type/d:before:opacity-0 group-data-[checked=true]/d:before:z-10 group-data-[checked=true]/d:before:bg-transparent group-[&[data-checked=true]+*]/d:before:bg-transparent dark:before:bg-neutral-700">{t7}{t10}</label>;
    $[26] = id;
    $[27] = t10;
    $[28] = t7;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== checked || $[31] !== t11 || $[32] !== t2) {
    t12 = <div className="group/d relative first-of-type:col-1 first-of-type:row-1 first-of-type:shadow-none" data-checked={checked}>{t2}{t11}</div>;
    $[30] = checked;
    $[31] = t11;
    $[32] = t2;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}